/**
 * API Error Types
 * Typed error classes for better error handling and user-friendly messages
 */

export interface APIErrorData {
  message: string;
  code?: string | null;
  status?: number;
}

/**
 * Base API Error class
 */
export class APIError extends Error {
  public readonly data: APIErrorData;
  public readonly status?: number;

  constructor(data: APIErrorData) {
    super(data.message);
    this.name = "APIError";
    this.data = data;
    this.status = data.status;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError);
    }
  }
}

/**
 * Authentication error (401)
 */
export class AuthenticationError extends APIError {
  constructor(message: string = "Authentication required") {
    super({ message, code: "AUTH_REQUIRED", status: 401 });
    this.name = "AuthenticationError";
  }
}

/**
 * Authorization error (403)
 */
export class AuthorizationError extends APIError {
  constructor(
    message: string = "You do not have permission to perform this action"
  ) {
    super({ message, code: "FORBIDDEN", status: 403 });
    this.name = "AuthorizationError";
  }
}

/**
 * Not found error (404)
 */
export class NotFoundError extends APIError {
  constructor(message: string = "Resource not found") {
    super({ message, code: "NOT_FOUND", status: 404 });
    this.name = "NotFoundError";
  }
}

/**
 * Validation error (400)
 */
export class ValidationError extends APIError {
  constructor(message: string = "Invalid request data") {
    super({ message, code: "VALIDATION_ERROR", status: 400 });
    this.name = "ValidationError";
  }
}

/**
 * Rate limit error (429)
 */
export class RateLimitError extends APIError {
  constructor(message: string = "Too many requests. Please try again later") {
    super({ message, code: "RATE_LIMIT", status: 429 });
    this.name = "RateLimitError";
  }
}

/**
 * Server error (500+)
 */
export class ServerError extends APIError {
  constructor(message: string = "An internal server error occurred") {
    super({ message, code: "SERVER_ERROR", status: 500 });
    this.name = "ServerError";
  }
}

/**
 * Maps HTTP status codes to appropriate error types
 */
export function mapStatusToError(status: number, message?: string): APIError {
  switch (status) {
    case 401:
      return new AuthenticationError(message);
    case 403:
      return new AuthorizationError(message);
    case 404:
      return new NotFoundError(message);
    case 400:
      return new ValidationError(message);
    case 429:
      return new RateLimitError(message);
    case 500:
    case 502:
    case 503:
    case 504:
      return new ServerError(message);
    default:
      return new APIError({
        message: message || `HTTP error ${status}`,
        status,
      });
  }
}

/**
 * Extracts error data from a fetch Response
 */
export async function extractErrorData(
  response: Response
): Promise<APIErrorData> {
  let errorData: APIErrorData = {
    message: `HTTP error ${response.status}`,
    code: null,
    status: response.status,
  };

  try {
    const responseBody = await response.json();
    errorData = {
      ...errorData,
      ...responseBody,
      message: responseBody.error || responseBody.message || errorData.message,
    };
  } catch (_e) {
    // Response not JSON, use status text if available
    errorData.message = response.statusText || errorData.message;
  }

  return errorData;
}
